import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Send, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const offsetH = -new Date().getTimezoneOffset() / 60;
const toLocal = (utc) => (((utc + offsetH) % 24) + 24) % 24;
const toUtc = (local) => Math.round((((local - offsetH) % 24) + 24) % 24);
const fmt = (h) => `${String(Math.floor(h)).padStart(2, "0")}:${h % 1 ? "30" : "00"}`;

export const DigestSchedule = ({ onSent }) => {
  const [s, setS] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/account/digest-settings").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  const save = async (patch) => {
    setS((p) => ({ ...p, ...patch }));
    try {
      await api.patch("/account/digest-settings", patch);
      toast.success("Digest schedule saved");
    } catch {
      toast.error("Could not save schedule");
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/signals/digest/send-now");
      if (data.skipped_empty) toast.info("Nothing fired in the last 24h, so no email was sent");
      else if (data.delivered_email) toast.success(`Digest emailed to ${s?.email}`);
      else toast.error("Email delivery failed. Please try again shortly");
      setS((p) => ({ ...p, last_digest_sent_at: data.sent_at }));
      onSent?.();
    } catch {
      toast.error("Could not send digest");
    } finally {
      setSending(false);
    }
  };

  if (!s) return null;
  const localHour = toLocal(s.digest_hour_utc);

  return (
    <div className="px-6 py-3 border-b border-white/5 flex items-center gap-5 flex-wrap text-xs" data-testid="digest-schedule">
      <label className="flex items-center gap-2 text-slate-300">
        <Switch checked={s.digest_enabled} onCheckedChange={(v) => save({ digest_enabled: v })} data-testid="digest-enabled-switch" />
        <CalendarClock className="w-3.5 h-3.5 text-emerald-300" /> Daily email
      </label>
      <label className="flex items-center gap-2 text-slate-400">
        at
        <select
          value={localHour}
          onChange={(e) => save({ digest_hour_utc: toUtc(Number(e.target.value)) })}
          disabled={!s.digest_enabled}
          data-testid="digest-hour-select"
          className="h-7 rounded-md bg-white/[0.03] border border-white/10 px-2 font-mono text-slate-200 disabled:opacity-40"
        >
          {Array.from({ length: 24 }, (_, i) => toLocal(i)).sort((a, b) => a - b).map((h) => (
            <option key={h} value={h} className="bg-[#0B1020]">{fmt(h)}</option>
          ))}
        </select>
        <span className="font-mono text-slate-500">local</span>
      </label>
      <label className="flex items-center gap-2 text-slate-400">
        <Switch checked={s.digest_skip_empty} onCheckedChange={(v) => save({ digest_skip_empty: v })} data-testid="digest-skip-empty-switch" />
        Skip empty days
      </label>
      <div className="ml-auto flex items-center gap-3">
        <span className="font-mono text-slate-500" data-testid="digest-last-sent">
          {s.last_digest_sent_at ? `Last sent ${new Date(s.last_digest_sent_at).toLocaleString()}` : "Never sent"}
        </span>
        <Button size="sm" onClick={sendNow} disabled={sending} data-testid="digest-send-now-btn" className="h-7 bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Send now
        </Button>
      </div>
    </div>
  );
};
